/**
 * NoteModal - Modal component for adding/editing routing notes
 *
 * Used to create and edit notes that override pickup dates,
 * track reasons, and document issues for chargeback defense.
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  Save,
  Trash2,
  FileText,
  AlertTriangle
} from 'lucide-react';

// Reason options for notes
const REASON_OPTIONS = [
  { value: '', label: 'Select reason...' },
  { value: 'carrier_no_show', label: 'Carrier No Show' },
  { value: 'rollover', label: 'Rollover' },
  { value: 'early_window', label: 'Early Window' },
  { value: 'carrier_late', label: 'Carrier Late' },
  { value: 'customer_request', label: 'Customer Request' },
  { value: 'warehouse_delay', label: 'Warehouse Delay' },
  { value: 'product_issue', label: 'Product Issue' },
  { value: 'other', label: 'Other' },
];

const RESPONSIBILITY_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'carrier', label: 'Carrier' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'customer', label: 'Customer' },
  { value: 'inmocean', label: 'InMocean' },
  { value: 'vendor', label: 'Vendor' },
];

// Helper to format reason value to display label
const getReasonLabel = (value) => {
  const option = REASON_OPTIONS.find(o => o.value === value);
  return option ? option.label : value;
};

const getResponsibilityLabel = (value) => {
  const option = RESPONSIBILITY_OPTIONS.find(o => o.value === value);
  return option ? option.label : value;
};

export default function NoteModal({
  isOpen,
  onClose,
  onSave,
  onDelete,
  order,           // The order this note is for (from BigQuery)
  existingNote,    // Existing note if editing, null if creating
}) {
  const [formData, setFormData] = useState({
    original_pickup_date: '',
    override_pickup_date: '',
    reason: '',
    responsibility: '',
    note_text: '',
    ticket_number: '',
    ticket_sent: false,
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (existingNote) {
        // Editing existing note
        setFormData({
          original_pickup_date: existingNote.original_pickup_date || '',
          override_pickup_date: existingNote.override_pickup_date || '',
          reason: existingNote.reason || '',
          responsibility: existingNote.responsibility || '',
          note_text: existingNote.note_text || '',
          ticket_number: existingNote.ticket_number || '',
          ticket_sent: existingNote.ticket_sent || false,
        });
      } else {
        // Creating new note - pre-fill original pickup date from order
        setFormData({
          original_pickup_date: order?.carrier_pu_date || '',
          override_pickup_date: '',
          reason: '',
          responsibility: '',
          note_text: '',
          ticket_number: '',
          ticket_sent: false,
        });
      }
      setShowDeleteConfirm(false);
    }
  }, [isOpen, existingNote, order]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        ...formData,
        // Include order context for the note
        po_number: order?.po_number || '',
        load_id: order?.load_id || '',
        mabd: order?.mabd || '',
        warehouse: order?.warehouse || order?.ship_point || '',
        destination: order?.po_dest || '',
        cases: order?.cases || 0,
      });
      onClose();
    } catch (error) {
      console.error('Failed to save note:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existingNote) return;
    setDeleting(true);
    try {
      await onDelete(existingNote.id);
      onClose();
    } catch (error) {
      console.error('Failed to delete note:', error);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (!isOpen) return null;

  // Safe value helper
  const safeValue = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object' && 'value' in val) return safeValue(val.value);
    return val;
  };

  return (
    <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: 9999 }}>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        style={{ zIndex: 9999 }}
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4" style={{ zIndex: 10000, position: 'relative' }}>
        <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-green-50 to-white rounded-t-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <FileText size={20} className="text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">
                    {existingNote ? 'Edit Routing Note' : 'Add Routing Note'}
                  </h2>
                  <p className="text-sm text-slate-500">
                    PO: {safeValue(order?.po_number) || '—'}
                    {order?.load_id && ` | Load: ${safeValue(order.load_id)}`}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Order Context */}
          <div className="px-6 py-3 bg-slate-50 border-b border-slate-200">
            <div className="grid grid-cols-5 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Dest:</span>
                <span className="ml-1 font-medium text-slate-700">{safeValue(order?.po_dest) || '—'}</span>
              </div>
              <div>
                <span className="text-slate-500">MABD:</span>
                <span className="ml-1 font-medium text-slate-700">{safeValue(order?.mabd) || '—'}</span>
              </div>
              <div>
                <span className="text-slate-500">Ship Date:</span>
                <span className="ml-1 font-medium text-slate-700">{safeValue(order?.carrier_pu_date) || '—'}</span>
              </div>
              <div>
                <span className="text-slate-500">Stage:</span>
                <span className={`ml-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                  safeValue(order?.routing_status) === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                  safeValue(order?.routing_status) === 'Scheduled' ? 'bg-green-100 text-green-700' :
                  safeValue(order?.routing_status) === 'Confirmed' ? 'bg-blue-100 text-blue-700' :
                  'bg-slate-100 text-slate-700'
                }`}>{safeValue(order?.routing_status) || '—'}</span>
              </div>
              <div>
                <span className="text-slate-500">Cases:</span>
                <span className="ml-1 font-medium text-slate-700">{safeValue(order?.cases) || '—'}</span>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-6 space-y-5">
            {/* Date Override Section */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Original Pickup Date
                </label>
                <input
                  type="text"
                  value={formData.original_pickup_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, original_pickup_date: e.target.value }))}
                  placeholder="MM/DD"
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Override Pickup Date
                  <span className="ml-1 text-xs text-amber-600 font-normal">(new date)</span>
                </label>
                <input
                  type="text"
                  value={formData.override_pickup_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, override_pickup_date: e.target.value }))}
                  placeholder="MM/DD"
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Reason and Responsibility */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Reason
                </label>
                <select
                  value={formData.reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  {REASON_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Responsibility
                </label>
                <select
                  value={formData.responsibility}
                  onChange={(e) => setFormData(prev => ({ ...prev, responsibility: e.target.value }))}
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  {RESPONSIBILITY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Note Text */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Note
              </label>
              <textarea
                value={formData.note_text}
                onChange={(e) => setFormData(prev => ({ ...prev, note_text: e.target.value }))}
                placeholder="Enter note details..."
                rows={3}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Ticket Tracking */}
            <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">
                Ticket Tracking
                <span className="ml-2 text-xs font-normal text-slate-500">(for chargeback defense)</span>
              </h4>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.ticket_sent}
                    onChange={(e) => setFormData(prev => ({ ...prev, ticket_sent: e.target.checked }))}
                    className="rounded border-slate-300 text-green-600 focus:ring-green-500"
                  />
                  Ticket Sent
                </label>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-slate-600">Ticket #:</label>
                  <input
                    type="text"
                    value={formData.ticket_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, ticket_number: e.target.value }))}
                    placeholder="WM-12345"
                    className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 w-32 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50/50 rounded-b-xl">
            <div className="flex items-center justify-between">
              {/* Delete Button (only for existing notes) */}
              <div>
                {existingNote && !showDeleteConfirm && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={16} />
                    Delete Note
                  </button>
                )}
                {existingNote && showDeleteConfirm && (
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={16} className="text-red-500" />
                    <span className="text-sm text-red-600">Delete this note?</span>
                    <button
                      onClick={handleDelete}
                      disabled={deleting}
                      className="px-3 py-1 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      {deleting ? 'Deleting...' : 'Confirm'}
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="px-3 py-1 text-sm text-slate-600 hover:bg-slate-200 rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              {/* Save/Cancel Buttons */}
              <div className="flex items-center gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  <Save size={16} />
                  {saving ? 'Saving...' : existingNote ? 'Update Note' : 'Save Note'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Export helper functions for use in other components
export { REASON_OPTIONS, RESPONSIBILITY_OPTIONS, getReasonLabel, getResponsibilityLabel };
